import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { ref, set, push, get } from 'firebase/database';
import { auth, database } from '../firebase';
import { sendWelcomeEmail } from '../utils/nodemailerService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hardcoded admin credentials
  const ADMIN_EMAIL = 'admin@nstp.edu';
  const ADMIN_PASSWORD = 'admin123';  // In a real app, this would be stored securely

  // Check if the current user is an admin
  const isAdmin = currentUser && currentUser.email === ADMIN_EMAIL;

  // Student registration with email notification
  function registerStudent(studentData) {
    return new Promise((resolve, reject) => {
      try {
        console.log('Registering new student:', studentData);
        
        // Create a new entry with auto-generated ID in the students collection
        const studentsRef = ref(database, 'students');
        const newStudentRef = push(studentsRef);
        
        // Add registration timestamp and status
        const studentDataWithMetadata = {
          ...studentData,
          registrationDate: new Date().toISOString(),
          status: 'Registered',
        };
        
        console.log('Generated student ID:', newStudentRef.key);
        
        // Save the data to Firebase
        set(newStudentRef, studentDataWithMetadata)
        .then(() => {
          console.log('Student registration successful');
          
          // Try to send welcome email
          const emailData = {
            email: studentData.email,
            firstName: studentData.firstName,
            lastName: studentData.lastName,
            nstpComponent: studentData.nstpComponent
          };
          
          // Attempt to send welcome email, but don't fail registration if it fails
          sendWelcomeEmail(emailData)
            .then(() => {
              console.log('Welcome email sent successfully');
              resolve({ 
                success: true, 
                id: newStudentRef.key,
                data: studentDataWithMetadata,
                emailSent: true
              });
            })
            .catch(emailError => {
              console.error('Failed to send welcome email:', emailError);
              resolve({ 
                success: true, 
                id: newStudentRef.key,
                data: studentDataWithMetadata,
                emailSent: false,
                emailError: emailError.message
              });
            });
        })
        .catch(error => {
          console.error('Error during student registration:', error);
          reject(error);
        });
      } catch (error) {
        console.error('Exception during student registration:', error);
        reject(error);
      }
    });
  }

  // Admin login function using hardcoded credentials
  function login(email, password) {
    return new Promise((resolve, reject) => {
      if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
        // Successfully authenticated
        const adminUser = {
          uid: 'admin-uid',
          email: ADMIN_EMAIL,
          displayName: 'Admin',
          isAdmin: true
        };
        setCurrentUser(adminUser);
        setUserRole('admin');
        resolve(adminUser);
      } else {
        // Authentication failed
        reject(new Error('Invalid email or password'));
      }
    });
  }

  // Logout function
  function logout() {
    return new Promise((resolve) => {
      setCurrentUser(null);
      setUserRole(null);
      resolve();
    });
  }

  useEffect(() => {
    // Check for stored admin session in localStorage
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      const adminUser = JSON.parse(storedUser);
      setCurrentUser(adminUser);
      setUserRole('admin');
    }
    
    setLoading(false);
    
    // Clean up function
    return () => {};
  }, []);

  // Store admin user in localStorage when it changes
  useEffect(() => {
    if (currentUser && currentUser.email === ADMIN_EMAIL) {
      localStorage.setItem('adminUser', JSON.stringify(currentUser));
    } else if (!currentUser) {
      localStorage.removeItem('adminUser');
    }
  }, [currentUser]);

  const value = {
    currentUser,
    userRole,
    isAdmin,
    registerStudent,
    login,
    logout,
    ADMIN_EMAIL  // Export admin email for use in other components
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 